import { useCallback, useEffect, useState } from "react";

import type { StructuredSearchStore } from "../../stores";

interface UseFilterBuilderEditorParams {
  store: StructuredSearchStore;
  inputRef: React.RefObject<HTMLInputElement | null>;
  isEditMode: boolean;
  isDateInput: boolean;
  isDateRangeInput: boolean;
  shouldShowValueDropdown: boolean;
}

export const useFilterBuilderEditor = ({
  store,
  inputRef,
  isEditMode,
  isDateInput,
  isDateRangeInput,
  shouldShowValueDropdown,
}: UseFilterBuilderEditorParams) => {
  const [isEditorActive, setIsEditorActive] = useState(isEditMode);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [inputRef]);

  useEffect(() => {
    if (isDateInput || isDateRangeInput) {
      setIsEditorActive(false);
    }
  }, [isDateInput, isDateRangeInput]);

  useEffect(() => {
    if (!inputRef.current || document.activeElement !== inputRef.current) {
      return;
    }

    setIsEditorActive(true);
    if (store.step === "idle") {
      store.openFieldSuggest();
    } else if (
      !isDateInput &&
      !isDateRangeInput &&
      (store.step === "key" || shouldShowValueDropdown)
    ) {
      store.setDropdownOpen(true);
      store.setKeyboardIndex(0);
    }
  }, [inputRef, isDateInput, isDateRangeInput, shouldShowValueDropdown, store, store.step]);

  useEffect(() => {
    if (!isEditMode || !store.editingFilterId || isDateInput || isDateRangeInput) {
      return;
    }

    setIsEditorActive(true);
    focusInput();
    if (
      store.step === "operator" ||
      store.step === "field" ||
      store.step === "key" ||
      (store.step === "value" && shouldShowValueDropdown)
    ) {
      store.setDropdownOpen(true);
      store.setKeyboardIndex(0);
    }
  }, [
    focusInput,
    isDateInput,
    isDateRangeInput,
    isEditMode,
    store,
    store.editingFilterId,
    store.step,
    shouldShowValueDropdown,
  ]);

  const handleFocus = useCallback(() => {
    setIsEditorActive(true);
    if (store.step === "idle") {
      store.openFieldSuggest();
    } else if (
      !isDateInput &&
      !isDateRangeInput &&
      (store.step === "key" || shouldShowValueDropdown)
    ) {
      store.setDropdownOpen(true);
      store.setKeyboardIndex(0);
    }
  }, [isDateInput, isDateRangeInput, shouldShowValueDropdown, store]);

  const handleBlur = useCallback(() => {
    if (!isEditMode && !isDateInput && !isDateRangeInput) {
      store.setDropdownOpen(false);
    }
  }, [isDateInput, isDateRangeInput, isEditMode, store]);

  const handleEditorFocusCapture = useCallback(() => {
    setIsEditorActive(true);
  }, []);

  const handleEditorBlurCapture = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      const nextTarget = event.relatedTarget as Node | null;
      if (nextTarget && event.currentTarget.contains(nextTarget)) {
        return;
      }

      setIsEditorActive(false);
      if (!isDateInput && !isDateRangeInput) {
        store.setDropdownOpen(false);
      }
    },
    [isDateInput, isDateRangeInput, store],
  );

  return {
    focusInput,
    handleBlur,
    handleEditorBlurCapture,
    handleEditorFocusCapture,
    handleFocus,
    isEditorActive,
  };
};
